import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/lib/toast";

const STEPS = ["Orden del Día", "Selección", "Fichas IA", "Documento", "Publicar y enviar"];
const TIPOS = ["Iniciativas", "Dictámenes", "Proposiciones", "Posicionamientos"];
const SINGULAR = { Iniciativas: "Iniciativa", Dictámenes: "Dictamen", Proposiciones: "Proposición", Posicionamientos: "Posicionamiento" };
const DET_LBL = { Iniciativas: "INICIATIVAS SOBRESALIENTES", Dictámenes: "DICTÁMENES APROBADOS DE RELEVANCIA", Proposiciones: "PROPOSICIONES RELEVANTES", Posicionamientos: "POSICIONAMIENTOS" };

export default function SintesisWizard() {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1);
  const [url, setUrl] = useState("");
  const [rawText, setRawText] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [documents, setDocuments] = useState([]);
  const [log, setLog] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extraido, setExtraido] = useState(false);
  const [resumen, setResumen] = useState({});
  const [items, setItems] = useState([]);
  const [extractionId, setExtractionId] = useState(null);
  const [sessionDate, setSessionDate] = useState(null);
  const [published, setPublished] = useState(false);
  // Una sola síntesis por extracción aunque se pulse «Publicar» dos veces.
  const saveKeyRef = useRef(crypto.randomUUID());
  const [genFichas, setGenFichas] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const addLog = (line) => setLog((l) => [...l, line]);
  useEffect(() => {
    // Documentos de la biblioteca que pueden servir de fuente (órdenes del día y síntesis subidas).
    api.get("/admin/library", { params: { kind: "sintesis", limit: 100 } }).then((r) => setDocuments(r.data.items || [])).catch(() => setDocuments([]));
  }, []);

  const extraer = async () => {
    if (extracting) return;
    setExtracting(true); setLog([]); setExtraido(false); setExtractionId(null); setSavedId(null); setPublished(false); setSessionDate(null);
    saveKeyRef.current = crypto.randomUUID();
    addLog("→ Conectando con la fuente…");
    addLog("→ IA: identificando puntos de la sesión…");
    try {
      const { data } = await api.post("/ai/extract-orden", { url: url || null, text: rawText || null, document_id: !rawText && !url && documentId ? documentId : null });
      setResumen(data.resumen || {});
      setItems(data.items || []);
      // Las fichas se generan contra la fuente guardada de esta extracción.
      setExtractionId(data.extraction_id || null);
      setSessionDate(data.session_date || null);
      addLog(`   ✓ ${data.resumen?.iniciativas ?? "?"} iniciativas · ${data.resumen?.dictamenes ?? "?"} dictámenes · ${data.resumen?.proposiciones ?? "?"} proposiciones · ${data.resumen?.posicionamientos ?? "?"} posicionamientos`);
      addLog("→ IA: clasificando por temas CCE (trabajo, justicia, seguridad, educación…)");
      if (data.warning) addLog("⚠ " + data.warning);
      addLog(`✓ Extracción completa. ${(data.items || []).filter((i) => i.seleccionado).length} puntos coinciden con la agenda empresarial.`);
      setExtraido(true);
      toast("✓ Orden del Día procesada por la IA");
    } catch (e) {
      addLog("✗ Error: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
      toast("Error al extraer con IA");
    } finally { setExtracting(false); }
  };

  const generarFichas = async () => {
    setGenFichas(true);
    try {
      const { data } = await api.post("/ai/fichas", { items, extraction_id: extractionId });
      setItems(data.items || items);
      toast("✓ Fichas generadas por la IA");
    } catch (e) {
      toast("Error al generar fichas: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setGenFichas(false); }
  };

  const next = async () => {
    if (paso === 2) {
      const pendientes = items.some((it) => it.seleccionado && (!it.ficha || !Object.keys(it.ficha).length));
      setPaso(3);
      if (pendientes) await generarFichas();
      return;
    }
    setPaso((p) => Math.min(5, p + 1));
  };

  const seleccionados = useMemo(() => items.filter((i) => i.seleccionado), [items]);
  const temas = useMemo(() => [...new Set(seleccionados.map((i) => i.tema))], [seleccionados]);
  const destacado = useMemo(() => [...seleccionados].sort((a, b) => (b.relevancia || 0) - (a.relevancia || 0)).slice(0, 3).map((i) => i.titulo + "."), [seleccionados]);

  const hoy = (sessionDate ? new Date(sessionDate.slice(0, 10) + "T12:00:00Z") : new Date()).toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: sessionDate ? "UTC" : undefined });
  const fechaLabel = "Congreso del Estado | " + hoy.charAt(0).toUpperCase() + hoy.slice(1);

  const publicar = async () => {
    setSaving(true);
    try {
      let id = savedId;
      if (!id) {
        const { data } = await api.post("/sintesis", { fecha_label: fechaLabel, resumen, temas, destacado, items, status: "borrador",
          extraction_id: extractionId, session_date: sessionDate ? sessionDate.slice(0, 10) : null, client_request_id: saveKeyRef.current });
        id = data.id; setSavedId(id);
      }
      const { data } = await api.post(`/sintesis/${id}/publish`);
      setPublished(true);
      toast(data.subtopics?.status === "failed" ? "Síntesis publicada. La extracción de subtemas requiere atención en Artículos." : `✓ Síntesis publicada · ${data.subtopics?.total || 0} subtemas disponibles para revisar en Artículos`);
    } catch (e) {
      toast("Error al publicar: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally { setSaving(false); }
  };

  const updateFicha = (itemIdx, key, value) => {
    setItems((its) => its.map((it, i) => i === itemIdx ? { ...it, ficha: { ...it.ficha, [key]: value } } : it));
  };

  return (
    <div data-testid="sintesis-wizard">
      <h1 className="page">Síntesis Legislativa <span className="ai-badge">✦ Automatizada con IA</span></h1>
      <p className="pagesub">Flujo automatizado: Orden del Día → selección de relevantes → fichas → documento → envío.</p>

      <div className="steps">
        {STEPS.map((s, i) => (
          <div key={s} className={`step ${paso === i + 1 ? "current" : ""} ${paso > i + 1 ? "done" : ""}`} data-testid={`wizard-step-${i + 1}`}>
            <span className="n">{i + 1}</span>{s}
          </div>
        ))}
      </div>

      <div className="panel wizard-body">
        {paso === 1 && (
          <div data-testid="wizard-paso-1">
            <h2>Paso 1 · Obtener el Orden del Día</h2>
            <p style={{ fontSize: 13.5, color: "var(--gray)", margin: "8px 0 16px" }}>
              Fuente: Congreso del Estado de BC → Trabajo Legislativo → Actas de Sesiones. Pega la URL de la sesión o el texto del Orden del Día; la IA extraerá iniciativas, dictámenes, proposiciones y posicionamientos.
            </p>
            <div className="src-row">
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="URL del Orden del Día" data-testid="orden-url-input" />
              <button className="btn btn-ai" onClick={extraer} disabled={extracting} data-testid="extract-ai-button">
                {extracting ? "✦ Extrayendo…" : "✦ Extraer con IA"}
              </button>
            </div>
            <textarea value={rawText} onChange={(e) => setRawText(e.target.value)} placeholder="…o pega aquí el texto del Orden del Día (opcional, tiene prioridad sobre la URL)"
              style={{ width: "100%", minHeight: 110, border: "2px dashed #b9c6d0", borderRadius: 10, padding: 16, background: "#fafcfd" }}
              data-testid="orden-text-input" />
            {documents.length > 0 && <label style={{ display: "block", marginTop: 12, fontSize: 13.5, color: "var(--gray)" }}>…o elige un documento de la Biblioteca (se usa si no hay texto ni URL):
              <select value={documentId} onChange={(e) => setDocumentId(e.target.value)} data-testid="orden-document-select"
                style={{ display: "block", width: "100%", marginTop: 6, padding: "10px 12px", border: "1.5px solid #e4e8ec", borderRadius: 6, background: "#fff" }}>
                <option value="">Sin documento</option>
                {documents.map((d) => <option key={d.id} value={d.id}>{d.title}{d.session_date ? ` · sesión ${d.session_date.slice(0, 10)}` : ""}</option>)}
              </select></label>}
            {log.length > 0 && <div className="log" data-testid="extract-log">{log.join("\n")}</div>}
          </div>
        )}

        {paso === 2 && (
          <div data-testid="wizard-paso-2">
            <h2>Paso 2 · Selección de temas relevantes</h2>
            <p style={{ fontSize: 13.5, color: "var(--gray)", margin: "8px 0 14px" }}>
              La IA preseleccionó los puntos alineados a los temas del CCE. Ajusta la selección si lo deseas.
            </p>
            {TIPOS.map((tipo) => {
              const its = items.map((it, i) => ({ ...it, _i: i })).filter((x) => x.tipo === tipo);
              if (!its.length) return null;
              return (
                <div key={tipo}>
                  <div className="type-h">{tipo} ({its.length} detectadas)</div>
                  {its.map((it) => (
                    <label key={it._i} className={`item-row ${it.seleccionado ? "sel" : ""}`} data-testid={`item-row-${it._i}`}>
                      <input type="checkbox" checked={!!it.seleccionado}
                        onChange={(e) => setItems((arr) => arr.map((x, j) => j === it._i ? { ...x, seleccionado: e.target.checked } : x))} />
                      <div><div className="tt">{it.titulo}</div><div className="mm">{it.meta} · <span className="pill tag">{it.tema}</span></div></div>
                      <div className="score"><b>{it.relevancia}%</b><small>relevancia IA</small></div>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {paso === 3 && (
          <div data-testid="wizard-paso-3">
            <h2>Paso 3 · Fichas generadas por IA <span className="ai-badge">✦ editable</span></h2>
            <p style={{ fontSize: 13.5, color: "var(--gray)", margin: "8px 0 14px" }}>
              Para cada punto seleccionado, la IA generó los campos de tu metodología. Haz clic sobre cualquier texto para editarlo.
            </p>
            {genFichas && <div className="log">✦ La IA está redactando las fichas de los {seleccionados.length} puntos seleccionados…</div>}
            {items.map((it, i) => it.seleccionado && (
              <div key={i} className="ficha" data-testid={`ficha-${i}`}>
                <h4>{SINGULAR[it.tipo] || it.tipo} · {it.titulo}</h4>
                {it.ficha && Object.keys(it.ficha).length ? Object.entries(it.ficha).map(([k, v]) => (
                  <div className="f-row" key={k}>
                    <b>{k}</b>
                    <span contentEditable suppressContentEditableWarning
                      onBlur={(e) => updateFicha(i, k, e.target.textContent)}>{v}</span>
                  </div>
                )) : !genFichas && <div className="f-row"><span style={{ color: "var(--gray)" }}>Sin ficha aún — presiona "Regenerar fichas".</span></div>}
              </div>
            ))}
            <button className="btn btn-ai sm" onClick={generarFichas} disabled={genFichas} data-testid="regenerate-fichas-button">
              {genFichas ? "✦ Generando…" : "✦ Regenerar fichas con IA"}
            </button>
          </div>
        )}

        {paso === 4 && (
          <div data-testid="wizard-paso-4">
            <h2 style={{ textAlign: "center" }}>Paso 4 · Vista previa del documento</h2>
            <p style={{ fontSize: 13.5, color: "var(--gray)", margin: "8px 0 18px", textAlign: "center" }}>Formato oficial de la Síntesis Legislativa con los logos institucionales.</p>
            <div className="doc" data-testid="doc-preview">
              <div className="doc-head">
                <img src="/assets/logo_cce.png" alt="CCE Tijuana" />
                <img src="/assets/logo_fidem.png" alt="FIDEM" />
              </div>
              <div className="doc-body">
                <div className="doc-title">Síntesis<br />Legislativa</div>
                <div className="doc-sub">{fechaLabel}</div>
                <div className="doc-intro">Información elaborada por el <u>Consejo Coordinador Empresarial de Tijuana</u> y <u>FIDEM</u>, Fideicomiso Empresarial del Estado de Baja California.</div>
                <div className="doc-grid">
                  <div className="doc-left">
                    <h5>RESUMEN</h5>
                    <div className="r"><b>{resumen.iniciativas ?? 0}</b> Iniciativas remitidas</div>
                    <div className="r"><b>{resumen.dictamenes ?? 0}</b> Dictámenes discutidos</div>
                    <div className="r"><b>{resumen.proposiciones ?? 0}</b> Proposiciones presentadas</div>
                    <div className="r"><b>{resumen.posicionamientos ?? 0}</b> Posicionamientos elaborados</div>
                    <div className="temas"><h5>TEMAS<br />ABORDADOS</h5><ul>{temas.map((t) => <li key={t}>{t}</li>)}</ul></div>
                  </div>
                  <div className="doc-right">
                    <h5>DESTACADO</h5>
                    <ol>{destacado.map((d, i) => <li key={i}>{d}</li>)}</ol>
                  </div>
                </div>
                <div className="doc-det">
                  <div className="det-title">Detalles de lo destacado</div>
                  {TIPOS.map((tipo) => {
                    const its = seleccionados.filter((x) => x.tipo === tipo && x.ficha && Object.keys(x.ficha).length);
                    if (!its.length) return null;
                    return (
                      <div key={tipo}>
                        <h6>{DET_LBL[tipo]}</h6>
                        {its.map((it, n) => (
                          <div className="art" key={n}>
                            <b className="t">{n + 1}. {it.titulo}</b>
                            {Object.entries(it.ficha).map(([k, v]) => <p key={k}><b>{k}:</b> {v}</p>)}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {paso === 5 && (
          <div data-testid="wizard-paso-5">
            <h2>Paso 5 · Publicar y enviar</h2>
            <div className="cards" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 16 }}>
              <div className="kpi" style={{ textAlign: "center", padding: 34 }}>
                <div style={{ fontSize: 34 }}>🌐</div>
                <b style={{ fontSize: 17, margin: "8px 0" }}>Publicar en el sitio</b>
                <span>Aparecerá en portada y en la sección Síntesis Legislativa.</span><br /><br />
                <button className="btn btn-navy" onClick={publicar} disabled={saving || published} data-testid="publish-sintesis-button">
                  {saving ? "Publicando…" : published ? "✓ Publicada" : "Publicar ahora"}
                </button>
                {published && savedId && <p style={{ marginTop: 12 }}><Link to={`/sintesis/${savedId}`} target="_blank" data-testid="view-published-sintesis">Ver síntesis publicada ↗</Link> · <Link to="/admin/articulos" data-testid="go-subtopics">Revisar subtemas en Artículos →</Link></p>}
              </div>
              <div className="kpi" style={{ textAlign: "center", padding: 34 }}>
                <div style={{ fontSize: 34 }}>✉️</div>
                <b style={{ fontSize: 17, margin: "8px 0" }}>Enviar por correo</b>
                <span>A tu base de datos y suscriptores.</span><br /><br />
                <button className="btn btn-green" onClick={() => navigate("/admin/correo")} data-testid="go-correo-button">Ir al módulo de envío →</button>
              </div>
            </div>
          </div>
        )}

        <div className="wiz-foot">
          <button className="btn btn-ghost" onClick={() => setPaso((p) => Math.max(1, p - 1))} disabled={paso === 1} data-testid="wizard-prev-button">← Anterior</button>
          <button className="btn btn-green" onClick={next} disabled={(paso === 1 && !extraido) || paso === 5} data-testid="wizard-next-button">
            {paso === 4 ? "Continuar a publicación →" : "Siguiente →"}
          </button>
        </div>
      </div>
    </div>
  );
}
